import { type ReactNode, useState, useCallback } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { IressEditor } from '@iress/ids-editor';
import { NavigableError } from '@/lib/errors/NavigableError';
import { PageShell } from '@/components/PageShell';
import { AiDisclaimer } from '@/components/AiDisclaimer';
import { useSummary } from '@/lib/hooks/useSummary';

import {
  IressStack,
  IressInline,
  IressText,
  IressButton,
  IressTabSet,
  IressTab,
  IressPill,
  IressSkeleton,
  IressStyled,
  IressPanel,
} from '@iress-oss/ids-components';

export const Route = createFileRoute('/$id/summarise')({
  component: MeetingSummarisePage,
});

function SummarySkeleton() {
  return (
    <IressStack gap="spacing.4" py="spacing.4">
      <IressSkeleton mode="text" textStyle="typography.heading.3" width="60%" />
      <IressSkeleton mode="text" width="100%" />
      <IressSkeleton mode="text" width="95%" />
      <IressSkeleton mode="text" width="90%" />
      <IressSkeleton mode="text" textStyle="typography.heading.4" width="50%" />
      <IressSkeleton mode="text" width="100%" />
      <IressSkeleton mode="text" width="80%" />
      <IressSkeleton mode="text" width="100%" />
      <IressSkeleton mode="text" width="70%" />
    </IressStack>
  );
}

/** Tabbed analysis panel with meeting summary, compliance, and attachments. */
function AnalysisPanel({
  meetingSummarySlot,
}: {
  meetingSummarySlot: React.ReactNode;
}) {
  return (
    <IressPanel p="spacing.5" stretch>
      <IressStack gap="spacing.2" stretch style={{ minHeight: 0 }}>
        <IressText element="h2" textStyle="typography.heading.3">
          AI-Powered Analysis
        </IressText>

        <IressTabSet
          defaultSelected={0}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
          panelStyle={{ scrollable: 'y', stretch: true }}
        >
          <IressTab label="Meeting Summary">{meetingSummarySlot}</IressTab>
          <IressTab label="Compliance Summary">
            Compliance summary will be available here.
          </IressTab>
          <IressTab
            label={
              <IressInline gap="spacing.2" verticalAlign="middle">
                <span>Attachments</span>
                <IressPill mode="info">2</IressPill>
              </IressInline>
            }
          >
            Attachments will be available here.
          </IressTab>
        </IressTabSet>
        <AiDisclaimer />
      </IressStack>
    </IressPanel>
  );
}

/** Rich text editor initialised from the AI-generated summary. */
function EditableMeetingSummary({ initialValue }: { initialValue: string }) {
  const [value, setValue] = useState(initialValue);
  return (
    <IressStyled mt="-spacing.1">
      <IressEditor
        aria-label="Meeting summary"
        format="markdown"
        value={value}
        onChange={setValue}
        borderRadius="none"
      />
    </IressStyled>
  );
}

function MeetingSummarisePage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { data: summaryData, transcript, isLoading } = useSummary();

  const handleEditFromPreview = useCallback(
    (target: 'summarise' | 'suggestions' | 'actions') => {
      if (target === 'suggestions' || target === 'actions') {
        void navigate({
          to: `/${id}/suggestions`,
          state: (prev) => ({ ...prev, activeTab: target }),
        });
      }
    },
    [navigate, id],
  );

  if (!transcript && !summaryData && !isLoading) {
    throw new NavigableError({
      message: 'No transcript available. Please upload a file first.',
      to: '/',
      search: { finalise: id },
      buttonLabel: 'Upload Transcript',
    });
  }

  let meetingSummarySlot: ReactNode;

  if (summaryData) {
    meetingSummarySlot = (
      <EditableMeetingSummary initialValue={summaryData.meetingSummary} />
    );
  } else {
    meetingSummarySlot = <SummarySkeleton />;
  }

  return (
    <PageShell
      title="Meeting Summary"
      subtitle="AI-Powered Summary"
      breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Summarise' }]}
      transcriptEntries={transcript?.content}
      showTranscriptLayout
      preview={{
        suggestions:
          summaryData?.suggestions.map((s) => ({
            ...s,
            status: 'pending',
          })) ?? [],
        actions:
          summaryData?.actions.map((a) => ({
            ...a,
            status: 'pending',
          })) ?? [],
        onEdit: handleEditFromPreview,
        hideEditTargets: ['summarise'],
        loading: !summaryData,
      }}
      actions={
        <IressButton mode="primary" element={Link} to={`/${id}/suggestions`}>
          Next
        </IressButton>
      }
    >
      <AnalysisPanel meetingSummarySlot={meetingSummarySlot} />
    </PageShell>
  );
}
